<app-drawer-layout
  data-testid="transaction-rule-drawer"
  [attr.data-mode]="mode"
  [title]="headerEyebrow()"
  [state]="isLoading() ? 'loading' : 'ready'"
>
  @if (ruleStateLabel(); as stateLabel) {
    <span
      drawerTrailing
      class="rounded-full bg-surface-recurring-expense-pill px-2 py-0.5 text-xs font-extrabold text-text-recurring-expense-pill"
      data-testid="transaction-rule-drawer-state"
      >{{ stateLabel }}</span
    >
  }
  @if (mode === 'edit' && rule()) {
    <!-- Pause and delete act immediately, so they live apart from the form's Save. -->
    <button
      drawerTrailing
      class="size-9! min-h-9! shrink-0 rounded-full! p-0!"
      appUiButton
      appearance="ghost"
      tone="neutral"
      type="button"
      aria-label="Rule actions"
      aria-haspopup="menu"
      data-testid="transaction-rule-drawer-actions"
      [disabled]="saving()"
      [appUiDropdownMenuTrigger]="ruleActionsMenu"
      align="end"
      side="bottom"
    >
      <app-ui-icon class="h-5 w-5" name="more-vertical" />
    </button>
  }

  @if (missingSource()) {
    <div data-testid="transaction-rule-drawer-missing">
      <h3 appUiHeading="card">This item is no longer available</h3>
      <p class="m-0 mt-1 text-body-sm text-text-muted">
        @if (!featureEnabled()) {
          Transaction rules are not enabled for this account.
        } @else if (mode === 'edit') {
          The rule may have been deleted.
        } @else {
          The suggestion was already accepted or dismissed.
        }
      </p>
    </div>
  } @else {
    <form class="flex flex-1 flex-col" (ngSubmit)="save()">
      <div>
        <div class="flex flex-col gap-8">
          <!-- Identity -->
          <div class="flex items-center gap-4">
            <app-merchant-avatar size="lg" [name]="form().merchant" [logoUrl]="merchantLogoUrl()" />
            <div class="min-w-0">
              <!-- Names the merchant; the fields and cards below don't repeat it. -->
              <h3 appUiHeading="card" class="truncate" data-testid="transaction-rule-drawer-title">
                {{ form().merchant.trim() || 'New rule' }}
              </h3>
              @if (mode === 'new') {
                <p class="m-0 text-body-sm text-text-muted">Sort one merchant into a budget automatically</p>
              } @else {
                <p class="m-0 text-body-sm text-text-muted">Matches this exact merchant name, ignoring case</p>
              }
            </div>
          </div>

          @if (pausedExplanation(); as explanation) {
            <div
              class="flex flex-col items-start gap-3 rounded-lg bg-surface-recurring-expense-pill px-3.5 py-4"
              data-testid="transaction-rule-drawer-paused-note"
            >
              <p class="m-0 text-sm leading-relaxed text-text-recurring-expense-pill">{{ explanation }}</p>
              @if (ruleState() === 'paused') {
                <button
                  appUiButton
                  appearance="outline"
                  tone="neutral"
                  size="control"
                  type="button"
                  data-testid="transaction-rule-drawer-resume"
                  [disabled]="saving()"
                  (click)="togglePaused()"
                >
                  Resume rule
                </button>
              }
            </div>
          }

          <!-- Transaction the drawer was opened from -->
          @if (mode === 'suggestion-from-transaction' && transaction(); as transaction) {
            <div
              class="flex items-center gap-4 rounded-lg border border-border-muted bg-surface-page px-4 py-2.5"
              data-testid="transaction-rule-drawer-transaction"
            >
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-semibold text-text-title">This transaction</span>
                <span class="mt-0.5 block text-xs text-text-muted"
                  >{{ transaction.date | date: 'MMM d' }} · Not sorted yet</span
                >
              </span>
              <span class="text-sm font-semibold tabular-nums text-text-title">{{
                transaction.amount | currency
              }}</span>
            </div>
          }

          @if (proposal(); as change) {
            @if (change.action === 'update') {
              <!-- The one place the update is described; evidence and save lines below don't restate it. -->
              <section
                class="flex flex-col items-start gap-2 rounded-lg border border-border-muted p-4"
                data-testid="transaction-rule-update-comparison"
              >
                <p class="m-0 text-sm font-bold text-text-title">
                  @if (proposalMovesBudget()) {
                    Move your rule to {{ selectedBucketName() }}
                  } @else {
                    Expand your {{ selectedBucketName() }} rule
                  }
                </p>
                @if (proposalMovesBudget()) {
                  <p class="m-0 flex flex-wrap items-baseline gap-x-2 text-sm" data-testid="transaction-rule-update-budget">
                    <span class="text-text-muted">{{ bucketName(change.currentBucketId!) }}</span>
                    <span class="text-text-subtle" aria-hidden="true">→</span>
                    <span class="sr-only">becomes</span>
                    <span class="font-extrabold text-text-title">{{ selectedBucketName() }}</span>
                  </p>
                }
                @if (proposalChangesAmounts()) {
                  <p class="m-0 flex flex-wrap items-baseline gap-x-2 text-sm tabular-nums" data-testid="transaction-rule-update-amounts">
                    <span class="text-text-muted">{{ proposalRange(change.currentAmountMin, change.currentAmountMax) }}</span>
                    <span class="text-text-subtle" aria-hidden="true">→</span>
                    <span class="sr-only">becomes</span>
                    <span class="font-extrabold text-text-title">{{ proposalRange(change.amountMin, change.amountMax) }}</span>
                  </p>
                }
                @if (change.targetRuleId; as targetRuleId) {
                  <button
                    type="button"
                    class="text-sm font-bold text-brand-accent-strong hover:underline"
                    data-testid="transaction-rule-update-open-rule"
                    (click)="openSuggestedRule(targetRuleId)"
                  >
                    Open the rule instead
                  </button>
                }
              </section>
            }
          }
          @if (!proposal() || proposal()?.action === 'create') {
            <!-- Fields -->
            <div class="flex flex-col gap-6">
              <!-- Only new rules take a merchant; suggestions and saved rules name it in the title above. -->
              @if (mode === 'new') {
                <div class="relative">
                  <app-ui-field label="Merchant" inputId="transaction-rule-merchant">
                    <input
                      id="transaction-rule-merchant"
                      appUiInput
                      type="text"
                      name="merchant"
                      autocomplete="off"
                      placeholder="Exact merchant name"
                      data-testid="transaction-rule-drawer-merchant"
                      [attr.aria-expanded]="merchantListOpen() && merchantMatches().length > 0"
                      aria-controls="transaction-rule-merchant-matches"
                      [ngModel]="form().merchant"
                      (ngModelChange)="onMerchantInput($event)"
                      (focus)="merchantListOpen.set(true)"
                      (blur)="onMerchantBlur()"
                    />
                  </app-ui-field>
                  @if (merchantListOpen() && merchantMatches().length > 0) {
                    <ul
                      id="transaction-rule-merchant-matches"
                      role="listbox"
                      class="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-surface-elevated py-1 shadow-overlay"
                      data-testid="transaction-rule-drawer-merchant-matches"
                    >
                      @for (merchant of merchantMatches(); track merchant.name) {
                        <li role="option" [attr.aria-selected]="false">
                          <button
                            type="button"
                            class="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-text-title hover:bg-surface-muted"
                            (mousedown)="$event.preventDefault()"
                            (click)="selectMerchant(merchant)"
                          >
                            <app-merchant-avatar
                              size="xs"
                              [name]="merchant.name"
                              [logoUrl]="merchant.logoUrl"
                            />
                            <span class="min-w-0 truncate">{{ merchant.name }}</span>
                          </button>
                        </li>
                      }
                    </ul>
                  }
                  <span class="mt-1 block text-xs text-text-muted"
                    >Matches this exact merchant name, ignoring case.</span
                  >
                </div>
              }

              <!-- Type + amount mode: stacked so the segmented controls never overflow the drawer -->
              <div class="flex flex-col gap-6">
                <app-ui-toggle-group
                  legend="Type"
                  stretch
                  data-testid="transaction-rule-drawer-type"
                  [options]="transactionTypeOptions"
                  [value]="form().transactionType"
                  (valueChange)="setTransactionType($event)"
                />
                <app-ui-toggle-group
                  legend="Amount"
                  stretch
                  data-testid="transaction-rule-drawer-amount-mode"
                  [options]="amountModeOptions"
                  [value]="form().amountMode"
                  (valueChange)="setAmountMode($event)"
                />
              </div>

              @if (form().amountMode === 'range') {
                <div class="grid grid-cols-2 gap-4" data-testid="transaction-rule-drawer-amount-fields">
                  <app-ui-field label="From" inputId="transaction-rule-amount-min">
                    <app-ui-currency-input
                      inputId="transaction-rule-amount-min"
                      name="amountMin"
                      placeholder="0"
                      testId="transaction-rule-drawer-amount-min"
                      [ngModel]="form().amountMin"
                      (ngModelChange)="patchForm({ amountMin: $event })"
                    />
                  </app-ui-field>
                  <app-ui-field label="To" inputId="transaction-rule-amount-max" [error]="amountError()">
                    <app-ui-currency-input
                      inputId="transaction-rule-amount-max"
                      name="amountMax"
                      placeholder="0"
                      testId="transaction-rule-drawer-amount-max"
                      [ngModel]="form().amountMax"
                      (ngModelChange)="patchForm({ amountMax: $event })"
                    />
                  </app-ui-field>
                </div>
              }

              <!-- Budget -->
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium leading-snug text-text-title" id="transaction-rule-budget-label"
                  >Budget</span
                >
                <app-ui-combobox
                  placeholder="Choose a budget"
                  searchPlaceholder="Search budgets"
                  emptyText="No eligible budgets."
                  appearance="outline"
                  size="touch"
                  data-testid="transaction-rule-drawer-budget"
                  describedBy="transaction-rule-budget-hint"
                  [fillWidth]="true"
                  width="100%"
                  [options]="bucketOptions()"
                  [value]="selectedBucketOption()"
                  (valueChange)="onBucketChange($event)"
                />
                <span id="transaction-rule-budget-hint" class="text-xs text-text-muted">
                  @if (form().transactionType === 'INCOME') {
                    Income rules can target income budgets only.
                  } @else {
                    Expense rules can target spending budgets only.
                  }
                </span>
              </div>
            </div>
          }
          <!-- Evidence -->
          @if (suggestion(); as suggestion) {
            <section
              class="overflow-hidden rounded-lg border border-border-muted"
              data-testid="transaction-rule-drawer-evidence"
            >
              <button
                type="button"
                class="flex w-full items-center gap-2 bg-surface-page px-3.5 py-2.5 text-left"
                [attr.aria-expanded]="evidenceExpanded()"
                (click)="evidenceExpanded.update(expanded => !expanded)"
              >
                <span class="flex-1 text-sm font-bold text-text-title">Why this was suggested</span>
                <app-ui-icon
                  class="h-3.5 w-3.5 text-text-muted transition-transform"
                  [class.rotate-90]="evidenceExpanded()"
                  name="chevron-right"
                />
              </button>
              @if (evidenceExpanded()) {
                <div class="flex flex-col gap-2 border-t border-border-muted px-3.5 py-4">
                  <p class="m-0 text-sm leading-relaxed text-text">{{ evidenceSummary() }}</p>
                  <!-- Merchant and budget are the same on every row; the sentence above names both. -->
                  @for (evidence of evidenceTransactions(); track evidence.id) {
                    <div
                      class="flex items-center gap-2.5 text-sm text-text-muted"
                      data-testid="transaction-rule-drawer-evidence-row"
                    >
                      <span class="flex-1">{{ evidence.date | date: 'MMM d' }}</span>
                      <span class="shrink-0 text-right font-bold tabular-nums text-text-title">{{
                        evidence.amount | currency
                      }}</span>
                    </div>
                  }
                  <p class="m-0 text-xs text-text-muted">{{ evidenceAmountLine() }}</p>
                </div>
              }
            </section>
          }

          <!-- Conflict / errors / warnings -->
          @if (conflict()) {
            <div
              class="flex gap-2.5 rounded-lg border border-status-danger/30 bg-status-danger/5 px-3.5 py-4"
              role="alert"
              data-testid="transaction-rule-drawer-conflict"
            >
              <app-ui-icon class="mt-0.5 h-6 w-6 shrink-0 text-status-danger" name="info" />
              <div class="flex flex-col gap-1">
                <span class="text-sm font-bold text-status-danger">This overlaps a rule you already have</span>
                <span class="text-sm leading-relaxed text-text">{{ conflictDescription() }}</span>
                @if (conflict()?.conflictingRuleId) {
                  <button
                    type="button"
                    class="mt-0.5 self-start text-sm font-bold text-brand-accent-strong hover:underline"
                    (click)="openConflictingRule()"
                  >
                    Open the
                    {{ conflict()?.conflictingRule ? bucketName(conflict()!.conflictingRule!.bucketId) : 'other' }} rule
                  </button>
                }
              </div>
            </div>
          } @else if (errorMessage(); as errorMessage) {
            <p
              class="m-0 rounded-lg border border-status-danger/30 bg-status-danger/5 px-3.5 py-4 text-sm text-status-danger"
              role="alert"
            >
              {{ errorMessage }}
            </p>
          }

          <!-- A changed recommendation is expected, not an error: say so and offer the refresh. -->
          @if (suggestionStale()) {
            <div
              class="flex flex-col items-start gap-3 rounded-lg bg-surface-tint px-3.5 py-4"
              role="status"
              data-testid="transaction-rule-drawer-stale"
            >
              <p class="m-0 text-sm leading-relaxed text-text">
                @if (suggestionUpdating()) {
                  Budgets is still updating this suggestion. Try again in a moment, or close this and check back
                  later.
                } @else {
                  This suggestion changed since you opened it. Refresh to see the latest recommendation.
                }
              </p>
              <button
                appUiButton
                appearance="outline"
                tone="neutral"
                size="control"
                type="button"
                data-testid="transaction-rule-drawer-refresh"
                (click)="refreshSuggestion()"
              >
                Refresh suggestion
              </button>
            </div>
          }

          @if (transactionExcludedByEdit() && transaction(); as transaction) {
            <div
              class="flex gap-2.5 rounded-lg bg-surface-recurring-expense-pill px-3.5 py-4"
              data-testid="transaction-rule-drawer-excluded"
            >
              <app-ui-icon class="mt-0.5 h-6 w-6 shrink-0 text-text-recurring-expense-pill" name="info" />
              <div class="flex flex-col gap-1">
                <span class="text-sm font-bold text-text-recurring-expense-pill"
                  >This transaction no longer matches</span
                >
                <span class="text-sm leading-relaxed text-text">
                  You opened this from the {{ transaction.amount | currency }} transaction on
                  {{ transaction.date | date: 'MMM d' }}. With these settings it stays unsorted. Widen the amounts to
                  include it, or sort it by hand afterwards.
                </span>
              </div>
            </div>
          }

          @if (saveLine(); as saveLine) {
            <p
              class="m-0 rounded-lg bg-surface-tint px-3.5 py-4 text-sm leading-relaxed text-text"
              data-testid="transaction-rule-drawer-save-line"
            >
              {{ saveLine }}
            </p>
          }
        </div>
      </div>

      <app-drawer-footer>
        <div class="flex w-full items-center gap-2">
          @if (mode === 'suggestion' || mode === 'suggestion-from-transaction') {
            <button
              appUiButton
              appearance="outline"
              tone="neutral"
              type="button"
              data-testid="transaction-rule-drawer-dismiss"
              [disabled]="saving()"
              (click)="dismiss()"
            >
              Dismiss
            </button>
          }
          <!-- Close (×) cancels; pause and delete live in the header menu. -->
          <span class="flex-1"></span>
          <button
            appUiButton
            appearance="solid"
            tone="primary"
            type="submit"
            data-testid="transaction-rule-drawer-save"
            [disabled]="saving() || !formValid()"
          >
            {{ primaryActionLabel() }}
          </button>
        </div>
      </app-drawer-footer>
    </form>
  }
</app-drawer-layout>

<ng-template #ruleActionsMenu>
  <div appUiDropdownMenu class="min-w-44" aria-label="Rule actions">
    <button appUiDropdownMenuItem type="button" data-testid="transaction-rule-drawer-toggle-paused" (click)="togglePaused()">
      {{ rule()?.enabled ? 'Pause rule' : 'Resume rule' }}
    </button>
    <button
      appUiDropdownMenuItem
      variant="destructive"
      type="button"
      data-testid="transaction-rule-drawer-delete"
      (click)="confirmDelete()"
    >
      Delete rule
    </button>
  </div>
</ng-template>
